// @ts-strict-ignore
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { UserContext } from '~/dashboard/context';
import apiCalls from '~/helpers/apiCalls';
import { typhonRoutes } from '~/helpers/routes';
import type { CustomQueryOpts } from '../@types/utils';

// ============================================================
//     TYPES
// ============================================================
type ApiResponse = {
  status_code: number;
  error: unknown | null;
  '_embedded': WorkflowFederatedMetadata;
};

export type WorkflowFederatedMetadata = {
  workflow_token: string;
  parent_workflow_token?: string | null;
  is_federated: boolean;
  is_published: boolean;
  published_to_child_accounts: ChildOrgDetails[];
};

type ChildOrgDetails = {
  organization_name: string;
  organization_slug: string;
  is_controlled_child: boolean;
  federated_workflow_active_version: number;
};

// ============================================================
//     QUERY
// ============================================================
const queryKey = ({
  workflowToken,
}) => [typhonRoutes.embeddedFinance.getWorkflowFederationStatus({ workflowToken })] as const;

/**
 * Get the AFEF federated status of a Workflow
 */
export const useGetWorkflowFederationStatusQuery = <
  TData extends WorkflowFederatedMetadata,
  TErr,
  TSel = TData,
>(
    { workflowToken },
    options: CustomQueryOpts<TData, TErr, TSel> = {},
  ) => {
  const { typhonDomain } = useContext(UserContext);

  return useQuery(
    {
      queryKey: queryKey({ workflowToken }),
      queryFn: async () => {
        const response = await apiCalls.embeddedFinance.getWorkflowFederationStatus({
          typhonDomain,
          workflowToken,
        }) as ApiResponse;

        if (response.status_code !== 200) {
          Promise.reject(response);
        }

        // eslint-disable-next-line no-underscore-dangle
        return response._embedded;
      },
      ...options,
    });
};

useGetWorkflowFederationStatusQuery.queryKey = queryKey;
